import React, { useEffect, useRef } from "react";

const NODES = [
  { x: 5, y: 8 }, { x: 15, y: 25 }, { x: 28, y: 12 }, { x: 42, y: 30 },
  { x: 55, y: 8 }, { x: 68, y: 22 }, { x: 82, y: 10 }, { x: 92, y: 28 },
  { x: 10, y: 45 }, { x: 25, y: 55 }, { x: 38, y: 42 }, { x: 50, y: 52 },
  { x: 65, y: 48 }, { x: 78, y: 58 }, { x: 90, y: 42 }, { x: 8, y: 72 },
  { x: 22, y: 80 }, { x: 35, y: 68 }, { x: 48, y: 78 }, { x: 60, y: 70 },
  { x: 75, y: 82 }, { x: 88, y: 68 }, { x: 95, y: 85 }, { x: 3, y: 92 },
  { x: 18, y: 98 }, { x: 33, y: 90 }, { x: 50, y: 95 }, { x: 70, y: 92 },
  { x: 85, y: 98 }, { x: 45, y: 15 }, { x: 72, y: 35 }, { x: 12, y: 60 },
  { x: 58, y: 62 }, { x: 30, y: 35 }, { x: 80, y: 75 }, { x: 20, y: 18 },
  { x: 65, y: 5 }, { x: 40, y: 60 }, { x: 95, y: 55 }, { x: 52, y: 38 },
];

const CONNECTIONS = [];
for (let i = 0; i < NODES.length; i++) {
  for (let j = i + 1; j < NODES.length; j++) {
    const dx = NODES[i].x - NODES[j].x;
    const dy = NODES[i].y - NODES[j].y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < 22) {
      CONNECTIONS.push([i, j, dist]);
    }
  }
}

export const NeuralBackground = () => {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const offsetsRef = useRef(
    NODES.map(() => ({
      ox: Math.random() * Math.PI * 2,
      oy: Math.random() * Math.PI * 2,
      sx: 0.3 + Math.random() * 0.4,
      sy: 0.3 + Math.random() * 0.4,
      amp: 1.5 + Math.random() * 2,
    }))
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let running = true;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = canvas.offsetWidth * dpr;
      canvas.height = canvas.offsetHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resize();
    window.addEventListener("resize", resize);

    const draw = (time) => {
      if (!running) return;
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      ctx.clearRect(0, 0, w, h);

      const t = time * 0.001;
      const offsets = offsetsRef.current;
      const positions = NODES.map((n, i) => {
        const o = offsets[i];
        return {
          x: (n.x / 100) * w + Math.sin(t * o.sx + o.ox) * o.amp,
          y: (n.y / 100) * h + Math.cos(t * o.sy + o.oy) * o.amp,
        };
      });

      // Lines
      for (const [i, j, dist] of CONNECTIONS) {
        const a = positions[i];
        const b = positions[j];
        const opacity = Math.max(0, 0.18 - dist * 0.005);
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.strokeStyle = `rgba(65, 156, 202, ${opacity})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      // Nodes
      for (let i = 0; i < positions.length; i++) {
        const p = positions[i];
        const pulse = 0.5 + 0.5 * Math.sin(t * 1.2 + i);

        // Glow
        ctx.beginPath();
        ctx.arc(p.x, p.y, 8, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(65, 156, 202, ${0.06 * pulse})`;
        ctx.fill();

        // Core
        ctx.beginPath();
        ctx.arc(p.x, p.y, 3, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(65, 156, 202, ${0.18 + 0.08 * pulse})`;
        ctx.fill();
      }

      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);

    return () => {
      running = false;
      if (animRef.current) cancelAnimationFrame(animRef.current);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 w-full h-full z-0 pointer-events-none"
      data-testid="neural-background"
    />
  );
};
